import { Button, Col, Row, Space, Table, TableColumnsType, Typography } from "antd";
import { Icon } from "@iconify/react";
import { useEffect, useState } from "react";

import { sorItemApi, toArray } from "../../../../Services";
import { useNotification } from "../../../../Hooks/useNotification";
import { InlineEdit, SorItem, WORK_ORDER_MAX_AMOUNT } from "../../Constants";
import { usePanelSettings } from "../../../../Hooks/usePanelSettings";
import { ProcurementApproachModal, SorCatalogEntry } from "./ProcurementApproachModal";
import styles from "../../WorkOrder.module.scss";
import vars from "../../../../Styles/variables.module.scss";

/** Thousands-separated display for the numeric SOR columns. */
const toNumber = (value: any) =>
  value === "" || value === null || value === undefined || isNaN(Number(value))
    ? value
    : Number(value).toLocaleString();

interface Props {
  items: SorItem[];
  setItems: (items: SorItem[]) => void;
  onBack: () => void;
  onNext: () => void;
  onSaveDraft: () => void;
  isSavingDraft?: boolean;
  inlineEdit?: InlineEdit;
  /** The work order's category (code or name) and region (name or id): the
   *  SOR items offered, and their rates, are that region's for that category. */
  category?: string;
  region?: string;
}

/**
 * Step 2 — SOR item details.
 *
 * The table is a plain antd <Table> rather than the shared <DataTable>: this is
 * an in-form editable list (no server paging, search, export or selection), so
 * DataTable's toolbar would be dead weight here. List *screens* still use
 * DataTable.
 *
 * SOR options come from the same `${panelApiUrl}/sorItem` catalog that
 * Manage Catalog manages, so the dropdown can't drift from the catalog.
 */
/** Long text stops at `rows` lines (four by default); the whole of it shows on hover. */
export const ClampedText = ({ text, rows = 4, maxWidth }: { text?: string; rows?: number; maxWidth?: number }) => (
  <Typography.Paragraph
    ellipsis={{ rows, tooltip: { title: text, overlayStyle: { maxWidth: 480 } } }}
    style={{ marginBottom: 0, maxWidth }}
  >
    {text || "-"}
  </Typography.Paragraph>
);

/**
 * The SOR line columns every SOR table shares (this step, Review & Submit, the
 * detail page). Fixed widths with Description taking the rest, so a long
 * description wraps inside its column instead of squeezing the others.
 */
export const sorLineColumns: TableColumnsType<SorItem> = [
  { title: "SOR ID", dataIndex: "sorId", width: 100 },
  { title: "SOR Item", dataIndex: "itemName", width: 160 },
  { title: "Description", dataIndex: "description", render: (value: string) => <ClampedText text={value} /> },
  { title: "UOM", dataIndex: "uom", width: 90, render: (value: string) => value || "-" },
  { title: "Quantity", dataIndex: "quantity", width: 100, render: toNumber },
  { title: "Rate", dataIndex: "rate", width: 110, render: toNumber },
  { title: "Amount", dataIndex: "amount", width: 120, render: toNumber },
];

export const SOR = ({
  items,
  setItems,
  onBack,
  onNext,
  onSaveDraft,
  isSavingDraft,
  inlineEdit,
  category,
  region,
}: Props) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<SorItem | null>(null);
  /* The whole catalog entry, not just its id and name: Item Name, Description
     and Rate are the catalog's to decide, and carrying only two of the four
     is what let a line drift from what Manage Catalog configured. */
  const [sorOptions, setSorOptions] = useState<SorCatalogEntry[]>([]);
  const { openNotification } = useNotification();

  useEffect(() => {
    const toOptions = (res: any) =>
      toArray(res).map((record: any) => ({
        sorId: String(record.sorId ?? ""),
        itemName: String(record.itemName ?? ""),
        description: String(record.description ?? ""),
        uom: String(record.uom ?? ""),
        rate: record.value,
      }));
    const wholeCatalog = () => sorItemApi.list({ size: 1000 }).then(toOptions);
    /* With a category and region chosen, /sorItem/lookup returns that
       category's items with `value` already set to the region's rate — the
       regional pricing matrix. Without either, or if the lookup is not there
       yet, the whole catalog at its base rate, as before. */
    (category && region ? sorItemApi.lookup(category, region).then(toOptions).catch(wholeCatalog) : wholeCatalog())
      .then(setSorOptions)
      .catch(() => {
        // Catalog unreachable — the dropdowns stay empty and the step is still
        // reviewable. Deliberately quiet: api.service already surfaces errors.
        setSorOptions([]);
      });
  }, [category, region]);

  const handleSave = (item: Omit<SorItem, "key">) => {
    if (editingItem) {
      setItems(items.map((row) => (row.key === editingItem.key ? { ...item, key: row.key } : row)));
    } else {
      setItems([...items, { ...item, key: `${Date.now()}-${items.length}` }]);
    }
    setEditingItem(null);
    setModalOpen(false);
  };

  const closeModal = () => {
    setEditingItem(null);
    setModalOpen(false);
  };

  /* Summed line amounts, and whether they breach the contract ceiling.
     Next stays disabled while over the cap — the warning explains why.

     The ceiling is the Contract Limit configured in Manage Catalog ->
     Settings, which is also what the server enforces. It used to be hardcoded
     at 3 million here, so changing the setting moved the server's limit and
     left this one behind. The constant is only the fallback for a settings
     call that has not answered yet. */
  const { contractLimit } = usePanelSettings();
  const limit = contractLimit ?? WORK_ORDER_MAX_AMOUNT;
  const totalAmount = items.reduce((sum, item) => sum + (Number(item.amount) || 0), 0);
  const isOverLimit = totalAmount > limit;

  const handleDelete = (key: string) => {
    setItems(items.filter((item) => item.key !== key));
    openNotification({ title: "Removed", message: "SOR item removed", type: "success" });
  };

  const columns: TableColumnsType<SorItem> = [
    ...sorLineColumns,
    {
      title: "Action",
      dataIndex: "action",
      width: 90,
      render: (_value: any, record: SorItem) => (
        <Space size="middle">
          <Icon
            icon="akar-icons:edit"
            inline
            fontSize={18}
            style={{ cursor: "pointer", color: vars.primary }}
            onClick={() => {
              setEditingItem(record);
              setModalOpen(true);
            }}
          />
          <Typography.Text type="danger">
            <Icon
              icon="fluent:delete-48-regular"
              inline
              fontSize={18}
              style={{ cursor: "pointer" }}
              onClick={() => handleDelete(record.key)}
            />
          </Typography.Text>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row
        justify="space-between"
        align="middle"
        style={{ marginBottom: 16 }}
      >
        <Col>
          <Typography.Title
            level={4}
            style={{ margin: 0 }}
          >
            SOR Item details
          </Typography.Title>
        </Col>
        <Col>
          <Button
            type="primary"
            onClick={() => setModalOpen(true)}
          >
            <Space>
              <Icon
                icon="akar-icons:plus"
                inline
              />
              Add
            </Space>
          </Button>
        </Col>
      </Row>

      <Table<SorItem>
        columns={columns}
        dataSource={items}
        pagination={false}
        rowKey="key"
        tableLayout="fixed"
        summary={() =>
          items.length ? (
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={6}>
                <Typography.Text strong>Total Amount</Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={6}>
                <Typography.Text strong>{totalAmount.toLocaleString()}</Typography.Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7}> </Table.Summary.Cell>
            </Table.Summary.Row>
          ) : null
        }
      />

      {isOverLimit && (
        <div className={styles.amountWarning}>
          <Icon
            icon="mdi:information-outline"
            inline
          />
          Maximum allowed amount is {limit.toLocaleString()}
        </div>
      )}

      <Row
        justify="end"
        gutter={16}
        className={styles.stepActions}
      >
        {inlineEdit ? (
          <Col>
            <Button onClick={inlineEdit.onCancel}>Cancel</Button>
          </Col>
        ) : (
          <>
            <Col>
              <Button onClick={onBack}>Back</Button>
            </Col>
            <Col>
              <Button
                loading={isSavingDraft}
                onClick={onSaveDraft}
              >
                Save as draft
              </Button>
            </Col>
          </>
        )}
        <Col>
          <Button
            type="primary"
            disabled={items.length === 0 || isOverLimit}
            loading={inlineEdit?.isSaving}
            onClick={onNext}
          >
            {inlineEdit ? "Save" : "Next"}
          </Button>
        </Col>
      </Row>

      <ProcurementApproachModal
        open={modalOpen}
        onCancel={closeModal}
        onSubmit={handleSave}
        sorOptions={sorOptions}
        initialValues={editingItem}
        existing={items.filter((row) => row.key !== editingItem?.key)}
      />
    </>
  );
};
